import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPatch } from "./api";
import "./agent-personalization.css";

const POSITIONING_OPTIONS = [
  ["generaliste", "Généraliste"], ["volume", "Volume / rotation"], ["premium", "Premium"],
  ["sport", "Sport / passion"], ["luxe", "Luxe"], ["electrique", "Hybride / électrique"],
  ["utilitaire", "Utilitaires"], ["import", "Import / cross-border"],
];
const OBJECTIVE_OPTIONS = [
  ["gain_temps", "Gagner du temps et réduire les tâches répétitives"],
  ["marge", "Améliorer la marge par véhicule"],
  ["sourcing", "Trouver de meilleurs véhicules plus vite"],
  ["conversion", "Mieux convertir les leads et prospects"],
  ["management", "Structurer l'équipe et le workflow"],
  ["finance", "Mieux piloter stock, trésorerie et performance"],
];
const SOURCE_OPTIONS = [
  ["marketplaces", "Marketplaces"], ["auctions", "Enchères"], ["ads", "Leads via Ads"],
  ["calls", "Appels / prospection"], ["tradeins", "Reprises clients"], ["network", "Réseau B2B"],
  ["imports", "Importateurs / Europe"], ["mandates", "Mandats / apporteurs"],
];
const COUNTRY_OPTIONS = ["France", "Allemagne", "Belgique", "Pays-Bas", "Luxembourg", "Espagne", "Italie", "Suisse"];
const BOTTLENECK_OPTIONS = [
  ["email", "Emails / messages"], ["sourcing", "Sourcing"], ["qualification", "Qualification des leads"],
  ["agenda", "Agenda / relances"], ["pricing", "Prix / valorisation"], ["stock", "Rotation du stock"],
  ["finance", "Trésorerie / marge"], ["team", "Coordination de l'équipe"],
];

const DEFAULT_ANSWERS = {
  role: "", employees: "", stock_size: "", stock_value: "", available_cash: "",
  monthly_sales_target: "", monthly_leads_target: "", avg_margin_target: "",
  positioning: [], preferred_brands: "", primary_objective: "", sourcing_sources: [],
  sourcing_countries: [], avg_purchase_budget: "", bottlenecks: [],
};

const QUESTIONS = [
  { key:"role", title:"Quel est votre rôle au quotidien ?", help:"VIQ.AI adapte le niveau de synthèse, de pilotage et de délégation.", type:"select", options:[["dirigeant","Dirigeant / fondateur"],["gerant","Gérant"],["directeur_vo","Directeur VO"],["responsable_achat","Responsable achats / sourcing"],["marchand","Marchand VO indépendant"],["autre","Autre"]] },
  { key:"employees", title:"Combien de personnes travaillent dans votre entreprise ?", help:"Cela aide VIQ.AI à calibrer les recommandations d'organisation et d'assignation.", type:"number", suffix:"personnes", min:1, max:1000 },
  { key:"stock_size", title:"Combien de véhicules avez-vous généralement en parc ?", help:"VIQ.AI utilisera cette taille de stock pour contextualiser rotation, priorités et sourcing.", type:"number", suffix:"véhicules", min:0, max:10000 },
  { key:"stock_value", title:"Quelle est la valeur approximative de votre parc ?", help:"Une estimation suffit. Cette donnée sert au pilotage financier et n'est pas affichée publiquement.", type:"money" },
  { key:"available_cash", title:"Quelle trésorerie mobilisable consacrez-vous généralement aux achats VO ?", help:"VIQ.AI peut ainsi éviter de suggérer un rythme de sourcing incohérent avec votre capacité d'achat.", type:"money" },
  { key:"monthly_sales_target", title:"Combien de ventes visez-vous par mois ?", help:"Cet objectif alimente automatiquement le pilotage de performance.", type:"number", suffix:"ventes / mois", min:0, max:10000 },
  { key:"monthly_leads_target", title:"Combien de leads souhaitez-vous qualifier par mois ?", help:"VIQ.AI pourra comparer l'activité réelle à votre besoin commercial.", type:"number", suffix:"leads / mois", min:0, max:100000 },
  { key:"avg_margin_target", title:"Quelle marge brute moyenne ciblez-vous par véhicule ?", help:"Cette cible est utilisée pour prioriser les opportunités de sourcing.", type:"money" },
  { key:"positioning", title:"Comment décririez-vous votre positionnement ?", help:"Vous pouvez sélectionner plusieurs approches.", type:"multi", options:POSITIONING_OPTIONS },
  { key:"preferred_brands", title:"Quelles marques privilégiez-vous ?", help:"Séparez les marques par des virgules. Elles serviront à créer vos premiers profils de sourcing.", type:"text", placeholder:"Ex. BMW, Mercedes, Audi, Peugeot" },
  { key:"primary_objective", title:"Quel est votre objectif principal avec VIQ.AI ?", help:"L'agent donnera plus de poids aux recommandations qui servent cet objectif.", type:"select", options:OBJECTIVE_OPTIONS },
  { key:"sourcing_sources", title:"Quelles sont vos principales sources de sourcing ?", help:"Sélectionnez tous les canaux réellement utilisés.", type:"multi", options:SOURCE_OPTIONS },
  { key:"sourcing_countries", title:"Dans quels pays sourcez-vous principalement ?", help:"Ces pays alimentent les profils de sourcing et les recherches européennes.", type:"multi", options:COUNTRY_OPTIONS.map(x=>[x,x]) },
  { key:"avg_purchase_budget", title:"Quel est votre budget d'achat moyen par véhicule ?", help:"VIQ.AI l'utilise comme plafond indicatif dans les profils de sourcing générés.", type:"money" },
  { key:"bottlenecks", title:"Où perdez-vous le plus de temps aujourd'hui ?", help:"VIQ.AI utilisera ces points de friction pour proposer des actions plus pertinentes.", type:"multi", options:BOTTLENECK_OPTIONS },
];

const cleanNumber = (v) => String(v ?? "").replace(/[^0-9]/g, "");
const formatMoney = (v) => {
  const n = Number(cleanNumber(v));
  return Number.isFinite(n) && n > 0 ? n.toLocaleString("fr-FR") : "";
};
const selected = (value, key) => Array.isArray(value) && value.includes(key);

function QuestionField({ q, value, onChange }) {
  if (q.type === "select") return (
    <div className="viq-agent-choice-grid single">
      {q.options.map(([id,label]) => <button key={id} type="button" className={value===id?"on":""} onClick={()=>onChange(id)}>{label}</button>)}
    </div>
  );
  if (q.type === "multi") return (
    <div className="viq-agent-choice-grid">
      {q.options.map(([id,label]) => <button key={id} type="button" className={selected(value,id)?"on":""} onClick={()=>onChange(selected(value,id)?value.filter(x=>x!==id):[...(Array.isArray(value)?value:[]),id])}>{label}</button>)}
    </div>
  );
  if (q.type === "money") return (
    <label className="viq-agent-input money"><input inputMode="numeric" value={formatMoney(value)} onChange={e=>onChange(cleanNumber(e.target.value))} placeholder="0"/><span>€</span></label>
  );
  if (q.type === "number") return (
    <label className="viq-agent-input"><input type="number" min={q.min} max={q.max} value={value} onChange={e=>onChange(e.target.value)} placeholder="0"/><span>{q.suffix}</span></label>
  );
  return <label className="viq-agent-input"><input type="text" value={value || ""} onChange={e=>onChange(e.target.value)} placeholder={q.placeholder || "Votre réponse"}/></label>;
}

function normalizeLoaded(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return { ...DEFAULT_ANSWERS, ...src,
    positioning:Array.isArray(src.positioning)?src.positioning:[],
    sourcing_sources:Array.isArray(src.sourcing_sources)?src.sourcing_sources:[],
    sourcing_countries:Array.isArray(src.sourcing_countries)?src.sourcing_countries:[],
    bottlenecks:Array.isArray(src.bottlenecks)?src.bottlenecks:[],
  };
}

export function AgentPersonalizationWizard({ open, initialAnswers, onClose, onComplete, required = false }) {
  const [answers, setAnswers] = useState(()=>normalizeLoaded(initialAnswers));
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(()=>{ if(open){ setAnswers(normalizeLoaded(initialAnswers)); setStep(0); setError(""); } },[open, initialAnswers]);
  const q = QUESTIONS[step];
  const value = answers[q?.key];
  const canContinue = useMemo(()=>{
    if (!q) return false;
    if (["employees","stock_size","monthly_sales_target","monthly_leads_target"].includes(q.key)) return String(value ?? "").trim() !== "";
    if (["stock_value","available_cash","avg_margin_target","avg_purchase_budget"].includes(q.key)) return Number(cleanNumber(value)) >= 0 && String(value ?? "").trim() !== "";
    if (["positioning","sourcing_sources","sourcing_countries","bottlenecks"].includes(q.key)) return Array.isArray(value) && value.length > 0;
    return String(value ?? "").trim().length > 0;
  }, [q, value]);
  if (!open) return null;
  const save = async () => {
    setSaving(true); setError("");
    try {
      const res = await apiPatch("/api/viqai/personalization", { answers, completed:true });
      if (!res?.success) throw new Error(res?.error || "Enregistrement impossible");
      try {
        window.dispatchEvent(new Event("viq-goals-updated"));
        window.dispatchEvent(new Event("viq-opportunities-changed"));
        window.dispatchEvent(new Event("viq-opportunities-updated"));
        window.dispatchEvent(new Event("viq-personalization-updated"));
      } catch (_) {}
      onComplete?.(res.profile || { answers, completed:true, derived:res.derived || null });
    } catch (e) { setError(e?.message || "Impossible d’enregistrer la personnalisation."); }
    finally { setSaving(false); }
  };
  return (
    <div className="viq-agent-overlay" role="dialog" aria-modal="true" aria-label="Personnaliser VIQ.AI">
      <div className="viq-agent-wizard">
        <div className="viq-agent-wizard-head">
          <div><span>VIQ.AI · PERSONNALISATION</span><h2>Faisons connaissance.</h2><p>15 réponses suffisent pour adapter les objectifs, le sourcing et les recommandations à votre activité.</p></div>
          {!required && <button type="button" className="viq-agent-close" onClick={onClose} aria-label="Fermer">×</button>}
        </div>
        <div className="viq-agent-progress"><i style={{width:`${((step+1)/QUESTIONS.length)*100}%`}}/><span>{step+1} / {QUESTIONS.length}</span></div>
        <div className="viq-agent-question">
          <div className="viq-agent-question-num">Question {step+1}</div>
          <h3>{q.title}</h3><p>{q.help}</p>
          <QuestionField q={q} value={value} onChange={v=>setAnswers(a=>({...a,[q.key]:v}))}/>
          {error && <div className="viq-agent-error">{error}</div>}
        </div>
        <div className="viq-agent-actions">
          <button type="button" className="secondary" onClick={()=>setStep(s=>Math.max(0,s-1))} disabled={step===0 || saving}>Précédent</button>
          <div className="viq-agent-action-right">
            {!required && <button type="button" className="ghost" onClick={onClose} disabled={saving}>Plus tard</button>}
            {step < QUESTIONS.length-1
              ? <button type="button" className="primary" disabled={!canContinue || saving} onClick={()=>setStep(s=>Math.min(QUESTIONS.length-1,s+1))}>Continuer →</button>
              : <button type="button" className="primary" disabled={!canContinue || saving} onClick={save}>{saving?"Personnalisation…":"Personnaliser VIQ.AI →"}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AgentPersonalizationPanel() {
  const [loading,setLoading]=useState(true);
  const [profile,setProfile]=useState(null);
  const [open,setOpen]=useState(false);
  const load=()=>{ setLoading(true); apiGet("/api/viqai/personalization").then(r=>setProfile(r?.profile||null)).catch(()=>setProfile(null)).finally(()=>setLoading(false)); };
  useEffect(load,[]);
  const a=profile?.answers||{};
  const summary=[
    a.employees ? `${a.employees} collaborateur${Number(a.employees)>1?"s":""}` : null,
    a.stock_size ? `${a.stock_size} VO en parc` : null,
    a.monthly_sales_target ? `${a.monthly_sales_target} ventes/mois` : null,
    a.avg_margin_target ? `${Number(a.avg_margin_target).toLocaleString("fr-FR")} € de marge cible` : null,
  ].filter(Boolean);
  return <div className="viq-agent-settings">
    <div className="viq-agent-settings-hero"><div><span>VIQ.AI PERSONNALISÉ</span><h3>Personnaliser son agent VIQ.AI</h3><p>VIQ.AI utilise votre contexte dirigeant pour prioriser les informations, proposer des objectifs cohérents et maintenir vos profils de sourcing à jour.</p></div><button type="button" onClick={()=>setOpen(true)}>{profile?.completed?"Modifier mes réponses":"Configurer VIQ.AI"} →</button></div>
    <div className="viq-agent-settings-grid">
      <div><b>Contexte métier</b><span>{loading?"Chargement…":summary.length?summary.join(" · "):"À configurer"}</span></div>
      <div><b>Objectifs</b><span>{profile?.derived?.goals_configured?"Synchronisés automatiquement":"Créés après la personnalisation"}</span></div>
      <div><b>Profils sourcing</b><span>{profile?.derived?.sourcing_profiles_count!=null?`${profile.derived.sourcing_profiles_count} profil(s) synchronisé(s)`:"Créés à partir de vos marques, pays et marge cible"}</span></div>
      <div><b>Réponses VIQ.AI</b><span>{profile?.completed?"Contextualisées à votre activité":"Personnalisation requise au premier échange"}</span></div>
    </div>
    <div className="viq-agent-settings-note">Vous gardez la main : modifier ces réponses met à jour le contexte VIQ.AI, les objectifs et les profils sourcing sans supprimer les profils issus de vos leads.</div>
    <AgentPersonalizationWizard open={open} initialAnswers={profile?.answers} onClose={()=>setOpen(false)} onComplete={p=>{setProfile(p);setOpen(false);}} />
  </div>;
}

export const PERSONALIZATION_QUESTIONS = QUESTIONS;
